import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Sparkles, Send, Bot, X } from 'lucide-react'
import { Textarea } from '@/components/ui/textarea'
import { useAskRize, type AskRizeMessage } from '@/hooks/useAskRize'
import { SupportTicketCard } from '@/components/layout/SupportTicketCard'
import { hasOpenTicket } from '@/lib/supportTicketCore'
import { renderAskRizeMarkdown } from '@/lib/askRizeMarkdown'
import { sanitizeAskRizeHtml } from '@/lib/sanitize'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'
// This panel had the app's ONLY AI disclosure before Laura item 6. It keeps its own
// wording (it must also say "read-only", which no other surface needs) but now carries
// the training's exact phrase, so every disclosure in the app makes the same claim.
import { AI_NOT_GUARANTEED } from '@/components/legal/AiOutputNotice'

// "Ask Rize" — the global read-only AI advisor slide-over. Opened from the
// header/sidebar Sparkles button or the Cmd+K palette via the `rize:ask`
// CustomEvent (ShortcutsHelp idiom; detail = optional prefill question).
// Conversation state is EPHEMERAL: closing the panel wipes it (useAskRize
// reset) — nothing persists client-side, by design. z-[80] sits above the
// palette (z-[60]) and shortcuts help (z-[70]).

// ⚠️ EVERY tool ai-assistant defines needs a row here. An unmapped tool falls through to
// `?? t` below and renders its RAW IDENTIFIER to the client — "get_listing_by_mls" in the
// activity line of a chat panel. `list_contacts` had been missing since it shipped and was
// doing exactly that. Coverage is pinned by tests/unit/mls-identifier-search.test.mjs, which
// reads the tool names out of the function itself, so a new tool fails the build rather than
// leaking a symbol into the UI.
const TOOL_LABELS: Record<string, string> = {
  search_contacts: 'Searched contacts',
  get_contact: 'Opened a contact',
  list_contacts: 'Listed contacts',
  list_transactions: 'Checked transactions',
  list_deadlines: 'Checked deadlines',
  list_tasks: 'Checked tasks',
  get_commission_summary: 'Checked commissions',
  list_referrals: 'Checked referrals',
  list_important_dates: 'Checked important dates',
  list_calendar_events: 'Checked calendar',
  search_listings: 'Searched listings',
  get_listing_by_mls: 'Looked up an MLS number',
  get_smart_matches: 'Checked smart matches',
  list_documents: 'Checked document inventory',
  search_documents: 'Searched document library',
  get_team_roster: 'Checked team roster',
  draft_support_ticket: 'Drafted a support ticket',
}

const DISCARD_DRAFT_PROMPT = 'Discard the draft ticket? It hasn’t been sent.'

const EXAMPLES = [
  'What’s on my plate today?',
  'Which deadlines are coming up this week?',
  'How do I create a smart list?',
]

export function AskRizeButton() {
  return (
    <button
      onClick={() => window.dispatchEvent(new CustomEvent('rize:ask'))}
      aria-label="Ask Rize — AI assistant"
      aria-haspopup="dialog"
      className="relative flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <Sparkles className="h-4 w-4" aria-hidden="true" />
    </button>
  )
}

export function AskRizePanel() {
  const { activeOrg } = useAuth()
  const { messages, sending, send, reset, ticketSent, dismissTicket } = useAskRize()
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const listRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    function handleOpen(e: Event) {
      const detail = (e as CustomEvent).detail
      previousFocusRef.current = document.activeElement as HTMLElement
      setOpen(true)
      if (typeof detail === 'string' && detail.trim()) setInput(detail.trim())
    }
    window.addEventListener('rize:ask', handleOpen)
    return () => window.removeEventListener('rize:ask', handleOpen)
  }, [])

  // Read by requestClose without re-subscribing the Escape listener on every streamed chunk.
  const messagesRef = useRef(messages)
  useEffect(() => { messagesRef.current = messages }, [messages])

  // Closing wipes the conversation (ephemeral by design), so while a draft ticket is still
  // open every way out — the X, Escape, the backdrop — asks first. A reflexive Escape in the
  // card's textarea must not silently lose a half-written ticket.
  const requestClose = useCallback(() => {
    if (hasOpenTicket(messagesRef.current) && !window.confirm(DISCARD_DRAFT_PROMPT)) return
    setOpen(false)
    setInput('')
    reset() // ephemeral: the conversation is gone on close
    previousFocusRef.current?.focus()
  }, [reset])

  // Escape closes (NotificationBell/ShortcutsHelp idiom).
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        requestClose()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, requestClose])

  // After a ticket is sent or dismissed its card unmounts with focus inside it; hand focus
  // back to the composer instead of dropping it to the page.
  function handleTicketSent(messageId: string, reference: string) {
    ticketSent(messageId, reference)
    textareaRef.current?.focus()
  }

  function handleTicketDismiss(messageId: string) {
    dismissTicket(messageId)
    textareaRef.current?.focus()
  }

  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => textareaRef.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [open])

  // Keep the newest message in view while streaming.
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight })
  }, [messages])

  function submit() {
    const q = input.trim()
    if (!q || sending) return
    setInput('')
    void send(q)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-label="Ask Rize — AI assistant">
      <div className="absolute inset-0 bg-black/60" onClick={requestClose} aria-hidden="true" />

      <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-border bg-card shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
            <h2 className="text-sm font-semibold">Ask Rize</h2>
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              Read-only
            </span>
          </div>
          <button
            onClick={requestClose}
            aria-label="Close Ask Rize"
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {/* AI disclosure (Idaho HB 127 house style) */}
        <div className="flex items-start gap-2 border-b border-primary/20 bg-primary/5 px-4 py-2 text-[11px] leading-snug text-muted-foreground">
          <Bot className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
          <span>
            AI-generated answers from your Rize data and the product guide, {AI_NOT_GUARANTEED}. Ask Rize is
            read-only — it can’t send, create, or change anything — and it isn’t legal or financial advice.
            Treat it as a starting point for your own checks, never as a finding.
          </span>
        </div>

        {/* Transcript */}
        <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4" aria-live="polite">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
              <Sparkles className="h-8 w-8 text-primary/60" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium">Ask about your Rize data, or how Rize works.</p>
                <p className="mt-1 text-xs text-muted-foreground">This conversation isn’t saved — it clears when you close the panel.</p>
                <p className="mt-1 text-xs text-muted-foreground">Something not working? Describe it here and Ask Rize will draft a ticket for the support team.</p>
              </div>
              <div className="flex w-full flex-col gap-2">
                {EXAMPLES.map((ex) => (
                  <button
                    key={ex}
                    onClick={() => void send(ex)}
                    disabled={sending || !activeOrg}
                    className="min-h-[44px] rounded-md border border-border px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {messages.map((m) => (
                <MessageBubble
                  key={m.id}
                  message={m}
                  organizationId={activeOrg?.organization_id ?? null}
                  onTicketSent={handleTicketSent}
                  onTicketDismiss={handleTicketDismiss}
                />
              ))}
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-border p-3">
          <div className="flex items-end gap-2">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  submit()
                }
              }}
              placeholder="Ask about your contacts, deals, deadlines…"
              aria-label="Ask Rize a question"
              rows={2}
              className="min-h-[44px] max-h-40 resize-none"
              maxLength={4000}
            />
            <button
              onClick={submit}
              disabled={!input.trim() || sending || !activeOrg}
              aria-label="Send question"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function MessageBubble({ message, organizationId, onTicketSent, onTicketDismiss }: {
  message: AskRizeMessage
  organizationId: string | null
  onTicketSent: (messageId: string, reference: string) => void
  onTicketDismiss: (messageId: string) => void
}) {
  const isUser = message.role === 'user'
  // Assistant answers are markdown-shaped (the fn's system prompt asks for
  // bullets; the model also emits **bold**, pipe tables, `code`). Render them
  // through the escape-first pipeline + DOMPurify allow-list. User turns and
  // error copy stay plain text. The memo is load-bearing: messages.map
  // re-invokes every bubble on each SSE chunk, so parsing must be confined to
  // the one bubble whose content actually changed.
  const isMarkdown = !isUser && message.status !== 'error'
  const html = useMemo(
    () => (isMarkdown && message.content ? sanitizeAskRizeHtml(renderAskRizeMarkdown(message.content)) : ''),
    [isMarkdown, message.content]
  )
  return (
    <div className={cn('flex flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
      {!isUser && message.tools.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {message.tools.map((t) => (
            <span key={t} className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
              {TOOL_LABELS[t] ?? t}
            </span>
          ))}
        </div>
      )}
      <div
        className={cn(
          'max-w-[92%] break-words rounded-lg px-3 py-2 text-sm leading-relaxed',
          !isMarkdown && 'whitespace-pre-wrap',
          isUser
            ? 'bg-primary/15 text-foreground'
            : message.status === 'error'
              ? 'border border-destructive/40 bg-destructive/10 text-foreground'
              : 'border border-border bg-muted/40 text-foreground'
        )}
      >
        {isMarkdown && message.content ? (
          <div dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          message.content || (message.status === 'streaming' ? <ThinkingDots /> : null)
        )}
        {message.status === 'streaming' && message.content && (
          <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-primary/70 align-middle" aria-hidden="true" />
        )}
      </div>
      {message.ticket?.status === 'open' && (
        <SupportTicketCard
          draft={message.ticket.draft}
          organizationId={organizationId}
          onSent={(reference) => onTicketSent(message.id, reference)}
          onDismiss={() => onTicketDismiss(message.id)}
        />
      )}
    </div>
  )
}

function ThinkingDots() {
  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground" aria-label="Ask Rize is thinking">
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current [animation-delay:0ms]" />
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current [animation-delay:150ms]" />
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current [animation-delay:300ms]" />
    </span>
  )
}
